import { ReleaseReservePopup } from "./popup/ReleaseReservePopup";
import { countryName, displayText } from "../locales/display";
import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import Pre from "@ui/pre/Pre";
import { Amount } from "@ui/amount/AmountCell";
import { Badge } from "@ui/badge/Badge";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { Table } from "@ui/table/Table";
import { TitledValue } from "@ui/table/TitledValue";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import Decimal from "decimal.js";
import { isValidElement, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { SETTINGS_ALLOWED_IDS } from "src/App";
import { checkFeature, TFeaturedComponent } from "src/Featuresmap";
import { formatAmount, savePdf } from "src/tools/tools";
import { IPaymentSession, IPaymentSessionMerchantChannel, IPaymentSessionStatementLink } from "src/types/types";
import { parseSessionDate } from "src/utils/sessionDate";
import { formatDisplayDate, formatDisplayDateTime, formatDisplayTime } from "src/utils/dateTime";
import {
	getRawDataForDisplay,
	getWebhookDisplayUrl,
	getWebhookForDisplay,
	shouldTransformWebhookDisplay,
	type TMerchantWebhookSettings,
} from "src/utils/webhookDisplay";
import { GD } from "../GD";
import { DECTA_TRANSACTIONS_ALLOWED_USER_IDS, OVERVIEW_ANALYSIS_USER_IDS, hasPermission, hasRole } from "../permissions";
import { notify } from "../tools/notify";
import { forceSessionWebhook } from "../tools/sessionActions";
import { PaymentSessionLog } from "./conponents/SessionLog";
import { CountryFlag } from "./conponents/icons/CountryFlag";
import { S_CHANGE_SESSION_STATUS_POPUP } from "./conponents/popup/ChangeSessionStatusPopup";
import { S_DECTA_SESSION_POPUP } from "./conponents/popup/DectaSessionPopup";
import { S_REFUND_POPUP } from "./conponents/popup/RefundPopup";
import { S_WEBHOOK_DETAIL_POPUP } from "./conponents/popup/WebhookDetailPopup";
import { S_CLIENT_CREDENTIALS_POPUP } from "./popup/ClientCredentialsPopup";
import { S_CONFIRM } from "./popup/general/ConfirmDialog";
import { TransactionAnalytics } from "./TransactionAnalytics";
import { CalendarClock, ChevronDown, ExternalLink, FileJson, FileText, RotateCw } from "lucide-react";
import { InterchangeDetails } from "./InterchangeDetails";
import { parseInterchangeData } from "./interchangeData";
import { S_RAW_TEXT_POPUP } from "./conponents/popup/RawTextPopup";
import { useTranslation } from "react-i18next";
import { EntityBanAction } from "./bans/EntityBanAction";
import styled from "styled-components";
import { AUTH_STATE } from "src/services/AuthService";
import { BankAnalysisPopup } from "./statView/BankAnalysisPopup";
import { getRankingAnalysisEntity, type BankAnalysisRequest } from "./statView/overviewBankAnalysis";
import { transactionAnalysisPeriod } from "./transactionAnalysis";
import { TransactionNavigationContext } from "./transactionNavigationContext";
import { StatementDialog } from "./reports/StatementPopup";
import { canAnalyzeTransactionFailure } from "../permissions";
import { TransactionFailureAnalysis, useTransactionFailureAnalysis } from "./TransactionFailureAnalysis";

const EntityAnalysisLink = styled.button`
	display: inline-flex; align-items: center; max-width: 100%; padding: 0;
	border: 0; background: transparent; color: #167ca6; font: inherit; text-align: left;
	overflow-wrap: anywhere; cursor: pointer; border-radius: 3px;
	&:hover { color: #105b7b; text-decoration: underline; text-underline-offset: 3px; }
	&:focus-visible { outline: 2px solid #167ca6; outline-offset: 3px; }
`;

const InfoRowLayout = styled.div`
	display: grid; grid-template-columns: 100px minmax(0, 1fr) auto; gap: 12px;
	align-items: center; padding: 10px 0;
	.info-value { min-width: 0; color: #0f172a; font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; text-align: left; }
	.info-action { justify-self: end; }
	@media (max-width: 480px) {
		grid-template-columns: 80px minmax(0, 1fr);
		.info-action { grid-column: 2; justify-self: start; }
		.info-action:empty { display: none; }
	}
`;

const emptyValue = "---";
const detailsColors = {
	border: "#e5e7eb",
	innerBorder: "#e2e8f0",
	muted: "#64748b",
	panel: "#FFFFFF",
	soft: "#f8fafc",
	text: "#0f172a",
	title: "#374151",
	blue: "#2563eb",
	shadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const copyColors = {
	background: "#bfdbfe",
	border: "#2563eb",
	icon: "#6885d4",
	text: "#2d50b9",
};

const EmptyLine = () => { useLocale(); return (<span aria-hidden="true" style={{ display: "inline-block", minHeight: "1em", width: "1px" }} />); };

const hasVisibleValue = (value: unknown): boolean => {
	if (value === 0) return true;
	if (!value) return false;
	if (Array.isArray(value)) return value.some(hasVisibleValue);
	if (isValidElement<{ children?: ReactNode }>(value)) {
		const props = value.props as { children?: ReactNode };
		if (Object.prototype.hasOwnProperty.call(props, "children")) return hasVisibleValue(props.children);
		return true;
	}
	return true;
};

const getValue = (value: unknown): ReactNode => {
	if (!hasVisibleValue(value)) return <EmptyLine />;
	if (isValidElement(value) || Array.isArray(value)) return value as ReactNode;
	if (typeof value === "object") return JSON.stringify(value);
	return value as ReactNode;
};

const hasRouteId = (value: unknown): boolean => hasVisibleValue(value) && value !== 0 && value !== "0";

const getRouteId = <T,>(value: T): T | undefined => (hasRouteId(value) ? value : undefined);

const copyText = async (value: string) => {
	if ("clipboard" in navigator) {
		await navigator.clipboard.writeText(value);
		return;
	}

	const input = document.createElement("textarea");
	input.value = value;
	input.style.position = "fixed";
	input.style.opacity = "0";
	document.body.appendChild(input);
	input.select();
	document.execCommand("copy");
	document.body.removeChild(input);
};

const CopyIndicatorIcon = ({ size = 14 }: { size?: number }) => {
	useLocale();
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" style={{ display: "block" }}>
			<rect x="9" y="9" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M15 5H7a2 2 0 0 0-2 2v8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
};

const CopyableText = ({ children, copyValue }: { children: ReactNode; copyValue?: string | number | null }) => {
	useLocale();
	const [hovered, setHovered] = useState(false);
	if (!copyValue && copyValue !== 0) return <>{children}</>;
	const value = String(copyValue);

	return (
		<span
			title={msg("copy")}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onClick={async event => {
				event?.stopPropagation();
				await copyText(value);
				notify.copied();
			}}
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: "4px",
				minWidth: 0,
				borderRadius: "999px",
				padding: "2px 6px",
				margin: "-3px -6px",
				cursor: "copy",
				transition: "background-color .12s, box-shadow .12s, color .12s",
				backgroundColor: hovered ? copyColors.background : "transparent",
				boxShadow: hovered ? `inset 0 0 0 1px ${copyColors.border}` : "inset 0 0 0 1px transparent",
				color: hovered ? copyColors.text : undefined,
			}}>
			{children}
			<span
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: "17px",
					height: "17px",
					color: hovered ? copyColors.text : detailsColors.muted,
					flexShrink: 0,
					transition: "color .12s",
				}}>
				<CopyIndicatorIcon size={17} />
			</span>
		</span>
	);
};

const countryNameFormatter = typeof Intl !== "undefined" && "DisplayNames" in Intl ? new Intl.DisplayNames(["en"], { type: "region" }) : null;

const getNormalizedCountryCode = (code?: string | null) => {
	if (!code) return null;
	const normalized = code.trim().toUpperCase();
	if (normalized.length !== 2 || !/^[A-Z]{2}$/.test(normalized)) return null;
	return normalized;
};

const getCountryName = (code?: string | null) => {
	if (!code) return null;
	const normalized = code.trim().toUpperCase();
	if (normalized.length !== 2) return null;
	try {
		return countryName(normalized, countryNameFormatter?.of(normalized) ?? normalized);
	} catch {
		return null;
	}
};

const CountryValue = ({ code }: { code?: string | null }) => {
	useLocale();
	const normalized = getNormalizedCountryCode(code);
	if (!code) return <>{emptyValue}</>;
	return (
		<HBox gap="6px" sx={{ alignItems: "center", flexWrap: "wrap" }}>
			{normalized ? <CountryFlag code={normalized} variant="rectangle" /> : null}
			<span>{code}</span>
		</HBox>
	);
};

const formatSessionDate = (value?: string | number | null) => {
	if (!value) return emptyValue;
	const date = parseSessionDate(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return formatDisplayDateTime(value, { separator: " " });
};

const formatSessionDateOnly = (value?: string | number | null) => {
	if (!value) return emptyValue;
	const date = parseSessionDate(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return formatDisplayDate(value);
};

const SessionDateTime = ({ value }: { value?: string | number | null }) => { useLocale(); return ((
	<DateTimeTooltip value={value || null}>{formatSessionDate(value)}</DateTimeTooltip>
)); };

const isReleaseSession = (session?: Pick<IPaymentSession, "_is_release"> | null) => session?._is_release === true;

const formatPaymentAmount = (payment: Pick<IPaymentSession, "amount" | "currency" | "type" | "net" | "_is_release">) => {
	if (isReleaseSession(payment)) {
		const net = payment.net === undefined || payment.net === null || payment.net === "" ? undefined : new Decimal(payment.net).abs();
		if (net) return formatAmount(net.toNumber()) + " " + payment.currency;
	}

	if (payment.amount === undefined || payment.amount === null) return emptyValue;
	const isRefund = payment.type?.toLowerCase() === "refund";
	return (isRefund ? "-" : "") + formatAmount(new Decimal(payment.amount).dividedBy(100).toNumber()) + " " + payment.currency;
};

const formatRefundAmount = (amount: number, currency?: string) => {
	return "-" + formatAmount(new Decimal(amount).dividedBy(100).toNumber()) + (currency ? " " + currency : "");
};

const formatOptionalMajorAmount = (amount?: number | string | null, currency?: string) => {
	if (amount === null || amount === undefined || amount === "") return emptyValue;
	const numericAmount = typeof amount === "number" ? amount : Number(amount);
	if (Number.isNaN(numericAmount)) return String(amount);
	return formatAmount(numericAmount) + (currency ? " " + currency : "");
};

const getStatusColor = (status?: string) => {
	const normalized = status?.toLowerCase();
	if (["completed", "success", "active", "approved"].includes(normalized ?? "")) return "#16a34a";
	if (["failed", "declined", "rejected", "error"].includes(normalized ?? "")) return "#dc2626";
	if (["pending", "created", "processing"].includes(normalized ?? "")) return "#d97706";
	return "#334155";
};

const getStatusTone = (status?: string): "success" | "warning" | "neutral" => {
	const normalized = status?.toLowerCase();
	if (["completed", "success", "settled", "approved"].includes(normalized ?? "")) return "success";
	if (["failed", "declined", "rejected", "error", "canceled", "refund_declined"].includes(normalized ?? "")) return "warning";
	return "neutral";
};

const PaymentStatusBadge = ({ status, color }: { status?: string; color: string }) => {
	useLocale();
	return (
		<HBox
			center
			gap="7px"
			sx={{
				backgroundColor: color,
				borderRadius: "20px",
				minHeight: "28px",
				padding: "7px 15px 7px 11px",
				whiteSpace: "nowrap",
			}}>
			<Box sx={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "rgba(255,255,255,0.9)", flexShrink: 0 }} />
			<Typo variant="medium" sx={{ color: "#fff", fontSize: "12px", fontWeight: 700, lineHeight: "14px", textTransform: "capitalize" }}>
				{status ? displayText(status) : emptyValue}
			</Typo>
		</HBox>
	);
};

const PaymentTypeBadge = ({ type }: { type?: string }) => {
	useLocale();
	const normalized = type?.toLowerCase();
	const isRefund = normalized === "refund";
	const isDmsOrTransfer = normalized === "dms" || normalized === "transfer";
	const bgColor = isRefund ? "rgba(38, 102, 220, 0.12)" : isDmsOrTransfer ? "rgba(22,163,74,0.12)" : "rgba(100,116,139,0.12)";
	const textColor = isRefund ? "#154fbb" : isDmsOrTransfer ? "#16A34A" : "#64748B";

	return (
		<Box sx={{ display: "inline-flex", alignItems: "center", minHeight: "28px", padding: "7px 15px", borderRadius: "20px", backgroundColor: bgColor }}>
			<Typo variant="medium" sx={{ color: textColor, fontSize: "12px", fontWeight: 700, lineHeight: "14px", textTransform: "uppercase" }}>
				{type ?? emptyValue}
			</Typo>
		</Box>
	);
};

const DashboardSection = ({ title, children }: { title: string; children: ReactNode }) => {
	useLocale();
	return (
		<VBox
			gap="12px"
			sx={{
				backgroundColor: detailsColors.panel,
				borderRadius: "10px",
				padding: "16px 18px",
				boxShadow: detailsColors.shadow,
			}}>
			<Typo variant="medium" sx={{ color: detailsColors.title, fontWeight: 600 }}>
				{title}
			</Typo>
			{children}
		</VBox>
	);
};

const NoDataBlock = () => {
	useLocale();
	return (
		<Box
			sx={{
				backgroundColor: detailsColors.soft,
				border: `1px solid ${detailsColors.innerBorder}`,
				borderRadius: "10px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				minHeight: "78px",
				padding: "12px 14px",
				gridColumn: "1 / -1",
			}}>
			<Typo variant="medium" sx={{ color: detailsColors.muted }}>{msg("noData")}</Typo>
		</Box>
	);
};

const MetricGrid = ({ children, empty = false }: { children: ReactNode; empty?: boolean }) => {
	useLocale();
	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
				gap: "12px",
			}}>
			{empty ? <NoDataBlock /> : children}
		</Box>
	);
};

const DetailsSurface = ({ children, padded = true }: { children: ReactNode; padded?: boolean }) => {
	useLocale();
	return (
		<VBox
			gap="12px"
			sx={{
				backgroundColor: detailsColors.panel,
				borderRadius: "10px",
				padding: padded ? "18px" : "0 14px",
				boxShadow: padded ? detailsColors.shadow : undefined,
			}}>
			{children}
		</VBox>
	);
};

const ReserveReleaseAction = styled.button.attrs({ type: "button" })`
 display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px;
 padding: 0; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; color: #475467; cursor: pointer;
 &:hover { background: #eff4ff; color: #175cd3; border-color: #b2ccff; }
 &:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
`;

const DetailCard = ({ title, value, subtitle, accent, onClick, action }: { title: string; value: unknown; subtitle?: unknown; accent?: string; onClick?: () => void; action?: ReactNode }) => {
	useLocale();
	return (
		<VBox
			gap="7px"
			title={onClick ? msg("openMerchant") : undefined}
			onClick={onClick}
			sx={{
				backgroundColor: detailsColors.soft,
				borderRadius: "10px",
				padding: "12px 14px", position: "relative",
				minHeight: "78px",
				border: `1px solid ${detailsColors.innerBorder}`,
				justifyContent: "flex-start",
				cursor: onClick ? "pointer" : undefined,
				transition: onClick ? "background-color .15s ease, border-color .15s ease, transform .15s ease" : undefined,
				"&:hover": onClick
					? {
							backgroundColor: "#f1f5f9",
							borderColor: "#94a3b8",
							transform: "translateY(-1px)",
						}
					: undefined,
			}}>
			<Typo variant="medium" sx={{ color: detailsColors.muted, fontWeight: 600, paddingRight: action ? "30px" : undefined }}>
				{title}
			</Typo>
			{action && <Box sx={{ position: "absolute", top: "8px", right: "10px" }}>{action}</Box>}
			<Typo variant="medium" bold sx={{ color: accent ?? detailsColors.text, lineHeight: "19px", minHeight: "19px", wordBreak: "break-word" }}>
				{getValue(value)}
			</Typo>
			<Typo variant="small" sx={{ color: detailsColors.muted, lineHeight: "17px", minHeight: "17px", fontSize: "12px", wordBreak: "break-word" }}>
				{getValue(subtitle)}
			</Typo>
		</VBox>
	);
};

const WideDetailRow = ({ title, value, copyValue, action }: { title: string; value: unknown; copyValue?: string | number | null; action?: ReactNode }) => {
	useLocale();
	return (
		<VBox
			gap="6px"
			sx={{
				backgroundColor: detailsColors.soft,
				border: `1px solid ${detailsColors.innerBorder}`,
				borderRadius: "10px",
				padding: "12px 14px",
			}}>
			<Typo variant="caption" bold sx={{ color: detailsColors.muted, textTransform: "uppercase", letterSpacing: ".08em" }}>
				{title}
			</Typo>
			<HBox gap="12px" sx={{ alignItems: "center", flexWrap: "wrap" }}>
				<span style={{ flex: "1 1 180px", minWidth: 0, color: detailsColors.text, fontSize: "12px", lineHeight: "17px", wordBreak: "break-all" }}>
					<CopyableText copyValue={copyValue}>{getValue(value)}</CopyableText>
				</span>
				{action}
			</HBox>
		</VBox>
	);
};

const InfoRow = ({ label, value, action, last = false }: { label: string; value: unknown; action?: ReactNode; last?: boolean }) => {
	useLocale();
	return (
		<InfoRowLayout style={{ borderBottom: last ? undefined : "1px solid #f0f0f0" }}>
			<Typo sx={{ color: "#6b7280", fontSize: "13px" }}>
				{label}
			</Typo>
			<div className="info-value">{getValue(value)}</div>
			<div className="info-action">{action}</div>
		</InfoRowLayout>
	);
};

const getStatementFields = (statement?: IPaymentSessionStatementLink | null) => {
	const fields: { key: keyof IPaymentSessionStatementLink; label: string; format?: "date" | "status" }[] = [
		{ key: "statement_id", label: msg("statementId") },
		{ key: "status", label: msg("status"), format: "status" },
		{ key: "statement_doc_status", label: msg("documentStatus"), format: "status" },
		{ key: "statement_type", label: msg("type") },
		{ key: "is_transition", label: msg("transition") },
		{ key: "period_start", label: msg("periodStart"), format: "date" },
		{ key: "period_end", label: msg("periodEnd"), format: "date" },
		{ key: "kind", label: msg("kind") },
		{ key: "linked_at", label: msg("linkedAt"), format: "date" },
	];
	return fields.map(field => ({ ...field, value: statement?.[field.key] }))
		.filter(field => field.value !== null && field.value !== undefined && (typeof field.value !== "string" || field.value.trim() !== ""));
};

const StatementSurface = styled.section`
	min-width: 0; padding: 18px; background: ${detailsColors.panel}; border-radius: 10px; box-shadow: ${detailsColors.shadow};
	font-size: 14px; line-height: 1.5; color: ${detailsColors.text};
	.statement-heading { display: flex; align-items: center; gap: 8px; margin: 0 0 16px; font-size: 14px; font-weight: 600; color: ${detailsColors.title}; }
	.statement-heading svg { color: ${detailsColors.muted}; flex-shrink: 0; }
	dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px 24px; margin: 0; }
	[data-statement-field] { min-width: 0; }
	dt { margin-bottom: 4px; font-size: 12px; color: ${detailsColors.muted}; }
	dd { margin: 0; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
	[data-statement-field="statement_id"] dd { font-weight: 600; }
	[data-statement-field="statement_id"] dd > span { max-width: 100%; box-sizing: border-box; }
	.statement-id { display: flex; align-items: center; gap: 10px; min-width: 0; }
	.statement-id > [title="Copy"] { min-width: 0; }
	.statement-open { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 28px; width: 28px; height: 28px; padding: 0; border: 1px solid ${detailsColors.border}; border-radius: 4px; background: transparent; color: ${detailsColors.blue}; cursor: pointer; }
	.statement-open:hover { background: ${detailsColors.soft}; border-color: ${detailsColors.blue}; }
	.statement-open:focus-visible { outline: 2px solid ${detailsColors.blue}; outline-offset: 2px; }
	.statement-dates { margin-top: 16px; padding-top: 14px; border-top: 1px solid ${detailsColors.border}; }
	.statement-links { margin-top: 18px; border-top: 1px solid ${detailsColors.border}; }
	.statement-links-title { display: flex; align-items: center; gap: 8px; margin: 14px 0 4px; font-size: 12px; font-weight: 600; color: ${detailsColors.muted}; }
	.statement-count { font-variant-numeric: tabular-nums; color: ${detailsColors.text}; }
	details + details { border-top: 1px solid ${detailsColors.border}; }
	summary { display: flex; align-items: center; gap: 10px; min-height: 48px; padding: 8px 4px; cursor: pointer; list-style: none; border-radius: 4px; }
	summary::-webkit-details-marker { display: none; }
	summary::marker { content: ""; }
	summary:hover { background: ${detailsColors.soft}; }
	summary:focus-visible { outline: 2px solid ${detailsColors.blue}; outline-offset: 3px; }
	.statement-link-label { min-width: 0; flex: 1; overflow-wrap: anywhere; font-weight: 600; font-variant-numeric: tabular-nums; }
	.statement-chevron { color: ${detailsColors.muted}; flex: 0 0 16px; transition: transform .15s ease; }
	details[open] > summary .statement-chevron { transform: rotate(180deg); }
	.statement-link-body { padding: 10px 0 18px; }
	@media (max-width: 480px) { dl { gap: 12px 16px; } .statement-dates { grid-template-columns: minmax(0, 1fr); } [data-statement-field="statement_id"] { grid-column: 1 / -1; } }
	@media (prefers-reduced-motion: reduce) { .statement-chevron { transition: none; } }
`;

const statementStatus = (status: string) => {
	const color = status === "paid" ? "#16a34a" : status === "calculated" ? "#d97706" : getStatusColor(status);
	return <span style={{ display: "inline-flex", maxWidth: "100%", padding: "3px 9px", borderRadius: "6px", color, backgroundColor: color + "14", fontSize: "12px", fontWeight: 600, textTransform: "capitalize", overflowWrap: "anywhere" }}>{status.replaceAll("_", " ")}</span>;
};

const StatementDetails = ({ statement, onOpenStatement }: { statement: IPaymentSession["statement_info"]; onOpenStatement?: (id: number) => void }) => {
	useLocale();
	const fields = getStatementFields(statement);
	const links = (statement?.links ?? []).map((entry, index) => ({ index, fields: getStatementFields(entry) }))
		.filter(link => link.fields.length > 0);
	const hasHistory = links.length > 1;
	if (!fields.length && !hasHistory) return null;

	const renderFields = (items: ReturnType<typeof getStatementFields>) => {
		const renderField = (field: (typeof items)[number]) => {
			let value: ReactNode = typeof field.value === "boolean" ? (field.value ? "Yes" : "No") : field.value;
			if (field.key === "statement_id") {
				const id = typeof field.value === "number" || typeof field.value === "string" && /^\d+$/.test(field.value.trim()) ? Number(field.value) : NaN;
				value = <span className="statement-id">
					<CopyableText copyValue={String(field.value)}><span style={{ minWidth: 0 }}>{value}</span></CopyableText>
					{onOpenStatement && Number.isSafeInteger(id) && id > 0 && <button type="button" className="statement-open" title={msg("templateOpenStatement_f6c51b3b", { v0: id })} aria-label={msg("templateOpenStatement_f6c51b3b", { v0: id })} aria-haspopup="dialog" onClick={event => { event.stopPropagation(); onOpenStatement(id); }}><ExternalLink size={14} aria-hidden="true" /></button>}
				</span>;
			}
			if (field.format === "date") {
				value = <DateTimeTooltip value={String(field.value)}>{formatDisplayDateTime(String(field.value), { fallback: String(field.value) })}</DateTimeTooltip>;
			}
			if (field.format === "status") value = statementStatus(String(field.value));
			return <div key={field.key} data-statement-field={field.key}><dt>{field.label}</dt><dd>{value}</dd></div>;
		};
		const metadata = items.filter(field => field.format !== "date");
		const dates = items.filter(field => field.format === "date");
		return <>
			{metadata.length > 0 && <dl>{metadata.map(renderField)}</dl>}
			{dates.length > 0 && <dl className="statement-dates">{dates.map(renderField)}</dl>}
		</>;
	};

	return (
		<StatementSurface aria-label={msg("statement")}>
			<h3 className="statement-heading"><FileText size={18} aria-hidden="true" />{msg("statement")}</h3>
			{renderFields(fields)}
			{hasHistory && <div className="statement-links">
				<h4 className="statement-links-title">{msg("statementHistory")}<span className="statement-count">{links.length}</span></h4>
				{links.map(link => {
					const id = link.fields.find(field => field.key === "statement_id")?.value;
					const status = link.fields.find(field => field.key === "status")?.value;
					return <details key={`${String(id ?? "")}:${link.index}`}>
						<summary>
							<span className="statement-link-label">{id !== undefined ? String(id) : msg("templateLinkedStatement_82b67821", { v0: link.index + 1 })}</span>
							{status !== undefined && statementStatus(String(status))}
							<ChevronDown className="statement-chevron" size={16} aria-hidden="true" />
						</summary>
						<div className="statement-link-body">{renderFields(link.fields)}</div>
					</details>;
				})}
			</div>}
		</StatementSurface>
	);
};

const ActivityItem = ({
	tone,
	title,
	description,
	meta,
	last = false,
}: {
	tone: "success" | "warning" | "neutral";
	title: string;
	description: ReactNode;
	meta?: ReactNode;
	last?: boolean;
}) => {
	useLocale();
	const colors = {
		success: "#16a34a",
		warning: "#d97706",
		neutral: detailsColors.muted,
	};

	return (
		<HBox gap="12px" sx={{ alignItems: "flex-start", padding: "12px 0", borderBottom: last ? undefined : "1px solid #f0f0f0" }}>
			<Box sx={{ width: "3px", alignSelf: "stretch", minHeight: "34px", borderRadius: "3px", backgroundColor: colors[tone], flexShrink: 0 }} />
			<VBox gap="4px" sx={{ minWidth: 0, flexGrow: 1 }}>
				<Typo variant="medium" bold sx={{ color: detailsColors.text }}>
					{title}
				</Typo>
				<Typo variant="small" sx={{ color: detailsColors.muted, lineHeight: "17px", fontSize: "12px", wordBreak: "break-word" }}>
					{description}
				</Typo>
			</VBox>
			{meta ? (
				<Typo variant="tiny" sx={{ color: colors[tone], whiteSpace: "nowrap" }}>
					{meta}
				</Typo>
			) : null}
		</HBox>
	);
};

const RelatedPaymentTypeCell = ({ payment }: { payment: IPaymentSession }) => {
	useLocale();
	const type = payment.type?.toLowerCase();
	const isRefund = type === "refund";
	const isDmsOrTransfer = type === "dms" || type === "transfer";
	const bgColor = isRefund ? "rgba(38, 102, 220, 0.12)" : isDmsOrTransfer ? "rgba(22,163,74,0.12)" : "rgba(100,116,139,0.12)";
	const textColor = isRefund ? "#154fbb" : isDmsOrTransfer ? "#16A34A" : "#64748B";

	return (
		<Box sx={{ display: "inline-flex", alignItems: "center", padding: "2px 10px", borderRadius: "12px", backgroundColor: bgColor }}>
			<Typo variant="caption" sx={{ color: textColor, fontWeight: 600 }}>
				{payment.type?.toUpperCase() ?? emptyValue}
			</Typo>
		</Box>
	);
};

const RelatedPaymentAmountCell = ({ payment }: { payment: IPaymentSession }) => {
	useLocale();
	const isRefund = payment.type?.toLowerCase() === "refund";
	const isGreen = payment.type?.toLowerCase() === "dms" || payment.type?.toLowerCase() === "transfer";
	const amountColor = isRefund ? "#DC2626" : isGreen ? "#16A34A" : "#1e293b";
	const prefix = isRefund ? "-" : "";

	return (
		<Typo
			variant="medium"
			sx={{
				color: amountColor,
				display: "block",
				fontVariantNumeric: "tabular-nums",
				fontWeight: 600,
				textAlign: "end",
				whiteSpace: "nowrap",
			}}>
			{payment.amount === undefined || payment.amount === null
				? emptyValue
				: prefix + formatAmount(new Decimal(payment.amount).dividedBy(100).toNumber())}
			<span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: "4px" }}>{payment.currency ?? ""}</span>
		</Typo>
	);
};

const RelatedPaymentsSection = ({ title, payments, onOpenSession }: { title: string; payments?: IPaymentSession[] | null; onOpenSession?: (session: IPaymentSession) => void }) => {
	useLocale();
	if (!payments?.length) return null;

	const headerLabels: Record<string, string> = {
		created_at: "Date",
		type: "Type",
		amount: "Amount",
	};

	return (
		<DashboardSection title={title}>
			<Table<IPaymentSession>
				headPadding="8px 12px"
				cellPadding="8px 12px"
				sx={{ backgroundColor: detailsColors.soft, borderRadius: "10px", overflow: "hidden" }}
				gridTemplateColumns="minmax(150px, 1fr) max-content minmax(120px, max-content)"
				order={["created_at", "type", "amount"]}
				headers={[{ value: "created_at" }, { value: "type" }, { value: "amount", sx: { textAlign: "right" } }]}
				headerFactory={val => {
					if (typeof val !== "string") return { content: null };
					const label = headerLabels[val] ?? val;
					return {
						content: (
							<Typo
								variant="medium"
								sx={{
									fontSize: "11px",
									fontWeight: 700,
									letterSpacing: "0.03em",
									textTransform: "uppercase",
									whiteSpace: "nowrap",
								}}>
								{label.toUpperCase()}
							</Typo>
						),
					};
				}}
				cellFactory={(payment, cell, _index, _key, name) => {
					if (name === "created_at") {
						const date = cell ? parseSessionDate(cell) : null;
						return {
							content: (
								<VBox sx={{ gap: "2px", minWidth: 0 }}>
									<Typo variant="medium" sx={{ color: detailsColors.text, fontSize: "13px", whiteSpace: "nowrap" }}>
										<DateTimeTooltip value={cell}>{formatDisplayDate(date, { withZone: false, fallback: emptyValue })}</DateTimeTooltip>
									</Typo>
									<Typo variant="caption" sx={{ color: "#94a3b8", fontSize: "11px", whiteSpace: "nowrap" }}>
										<DateTimeTooltip value={cell}>{formatDisplayTime(date, { fallback: "" })}</DateTimeTooltip>
									</Typo>
								</VBox>
							),
						};
					}
					if (name === "type") return { content: <RelatedPaymentTypeCell payment={payment} /> };
					if (name === "amount") return { content: <RelatedPaymentAmountCell payment={payment} /> };
					return { content: null };
				}}
				onRowClick={payment => {
					if (onOpenSession) onOpenSession(payment);
					else GD.S_TRANSACTION_PREVIEW.invoke(payment);
				}}
				data={payments}
			/>
		</DashboardSection>
	);
};

export const TransactionPreview = () => {
	useLocale();
	const session = GD.S_TRANSACTION_PREVIEW.use([]);
	return <TransactionPopup session={session} onClose={() => GD.S_TRANSACTION_PREVIEW.invoke(null)} />;
};

export const TransactionPopup = ({ session, onClose, onOpenSession, onExit }: { session: IPaymentSession | null; onClose: () => void; onOpenSession?: (session: IPaymentSession) => void; onExit?: () => void }) => {
	useLocale();
	const [response, setResponse] = useState<any>(null);
	const [releaseTarget, setReleaseTarget] = useState<{ id: number; projectId: number; userId: number } | null>(null);
	const [responseContext, setResponseContext] = useState<{ projectId: number; userId: number } | null>(null);
	const sessionRequest = useRef<AbortController | null>(null);
	const [state, setState] = useState<"small" | "full">("small");
	const [busy, setBusy] = useState(false);
	const [webhooksReloadTrigger, setWebhooksReloadTrigger] = useState(0);
	const [merchantWebhooks, setMerchantWebhooks] = useState<TMerchantWebhookSettings>();
	const contentRef = useRef<HTMLDivElement>(null);

	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const userProject = project && user ? user.projects.find(val => val.id === project.id) : undefined;
	const canChangeSessionStatus = hasRole(userProject?.roles, "admin");
	useEffect(() => { setReleaseTarget(null); }, [session?.id, project?.id, user?.id]);
	const canViewDectaTransactions = !!user && DECTA_TRANSACTIONS_ALLOWED_USER_IDS.includes(user.id);
	const failureAnalysis = useTransactionFailureAnalysis(
		session && response?.data?.id === session.id ? response.data : session, project?.id, user?.id,
		!busy && !!session && response?.data?.id === session.id && responseContext?.projectId === project?.id && responseContext?.userId === user?.id,
	);

	const loadSession = useCallback((sessionId: string | number) => {
		sessionRequest.current?.abort();
		setResponseContext(null);
		if (!project || !user) return;
		const controller = new AbortController(); sessionRequest.current = controller;
		setBusy(true);
		setResponse(null);
		const p = GD.REQ_API.request({
			method: "session/get",
			expectedProjectId: project.id, signal: controller.signal,
			data: { id: sessionId },
		});
		p.then(r => {
			if (controller.signal.aborted) return;
			setBusy(false);
			setResponse(r);
			setResponseContext({ projectId: project.id, userId: user.id });
		}).catch(() => { if (!controller.signal.aborted) { setBusy(false); setResponse(null); } });
	}, [project?.id, user?.id]);

	useEffect(() => {
		if (!session) return;
		loadSession(session.id);
		return () => sessionRequest.current?.abort();
	}, [loadSession, session]);

	const merchantId = response?.data?.mid ?? session?.mid;
	useEffect(() => {
		let active = true;
		setMerchantWebhooks(undefined);
		if (!shouldTransformWebhookDisplay() || merchantId === undefined || merchantId === null) return;

		GD.REQ_MERCHANT_SETTINGS_GET.request({ merchantID: merchantId, type: "credentials" }).then(settingsResponse => {
			if (!active) return;
			setMerchantWebhooks((settingsResponse?.data?.data as { webhooks?: TMerchantWebhookSettings } | undefined)?.webhooks);
		});

		return () => {
			active = false;
		};
	}, [merchantId]);

	useEffect(() => {
		if (!session) return;
		const scrollTop = () => {
			const root = contentRef.current;
			if (!root) return;

			const resetScroll = (el: Element | null) => {
				if (el instanceof HTMLElement) {
					el.scrollTop = 0;
					el.scrollLeft = 0;
				}
			};

			resetScroll(root);
			// Stop at the popup's fixed overlay: everything above it is the page, whose scroll position must survive opening the popup.
			let parent = root.parentElement;
			while (parent && window.getComputedStyle(parent).position !== "fixed") {
				resetScroll(parent);
				parent = parent.parentElement;
			}

			for (const el of root.querySelectorAll("*")) {
				const styles = window.getComputedStyle(el);
				if (styles.overflowY === "auto" || styles.overflowY === "scroll" || styles.overflow === "auto" || styles.overflow === "scroll") {
					resetScroll(el);
				}
			}
		};
		window.requestAnimationFrame(scrollTop);
		const timeoutID = window.setTimeout(scrollTop, 0);
		return () => window.clearTimeout(timeoutID);
	}, [session?.id, response?.data?.id]);

	useEffect(() => {
		if (!session) return;
		const handler = GD.S_TRANSACTIONS_UPDATE.subscribe(() => {
			loadSession(session.id);
		});
		return () => {
			GD.S_TRANSACTIONS_UPDATE.unsubscribe(handler);
		};
	}, [loadSession, session]);

	//const [response, _reload, _reset, busy] = GD.REQ_API.useRequest(null, { method: "session/get", data: { id: session?.id } });
	if (!session) return null;
	const currentSession = (response?.data?.id === session.id ? response.data : session) as IPaymentSession;
	const isRelease = isReleaseSession(currentSession);
	const canReleaseReserve = canChangeSessionStatus && !busy && !response?.error && response?.data?.id === session.id
		&& responseContext?.projectId === project?.id && responseContext?.userId === user?.id
		&& (!currentSession.project_id || currentSession.project_id === project?.id) && !isRelease && hasVisibleValue(currentSession.reserve_release_date);

	let sx: RecursiveCSS = {};
	if (state === "small") {
		sx = { maxWidth: "820px", minWidth: "420px", width: "99vw", minHeight: "90vh", maxHeight: "90vh" };
	} else {
		sx = { maxWidth: "920px", minWidth: "420px", width: "99vw", minHeight: "90vh", maxHeight: "90vh" };
	}

	sx = {
		...sx,
		padding: "0",
		backgroundColor: "#f3f4f6",
		transition: "all .2s",
	};

	if (!session) return null;

	const closePopup = () => {
		setBusy(false);
		setResponse(null);
		onClose();
	};
	const openMerchantPopup = (merchantID: number) => {
		(onExit ?? closePopup)();
		S_CLIENT_CREDENTIALS_POPUP.invoke({ merchantID });
	};

	const handleForceWebhook = () => {
		S_CONFIRM.invoke({
			title: msg("forceWebhook"),
			message: msg("areYouSureYouWantToForceWebhookForThisSession"),
			confirm: msg("forceWebhook"),
			reject: "Cancel",
			onResult: async val => {
				if (val !== "confirm") return;
				const webhookResponse = await forceSessionWebhook(session.id);
				if (webhookResponse && !webhookResponse.error) {
					setWebhooksReloadTrigger(prev => prev + 1);
				}
			},
		});
	};

	return (
		<TransactionNavigationContext.Provider value={onExit ?? closePopup}>
		<Panel
			sx={sx}
			contentPadding="0px 20px 20px 20px"
			scrollable
			decorator="absolute"
			onClose={closePopup}
			label={msg("payment_8efbcb37") + currentSession.id}
			busy={busy}>
			<div ref={contentRef} style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: "1 1 auto" }}>
				<Spacer size="16px" />
				{!isRelease && <Error error={response?.error} />}
				<Tabs
					variant="modern"
					onSectionSelect={section => {
						if (section > 1) {
							setState("full");
						} else setState("small");
					}}
					checkFeature={feature => checkFeature(feature as any)}
					scrollable
					spaceBottom={20}>
					<TabItem title={msg("details_14fdc0f6")}>
						<SessionDetails session={currentSession} onReleaseReserve={canReleaseReserve && project && user ? () => setReleaseTarget({ id: session.id, projectId: project.id, userId: user.id }) : undefined} onOpenMerchant={openMerchantPopup} onOpenSession={onOpenSession} banContext={!busy && response?.data?.id === session.id && !isRelease ? responseContext : null} />
					</TabItem>
					<TabItem title={msg("raw_ac0562bb")}>
						<RawSection response={response ?? { data: currentSession }} merchantWebhooks={merchantWebhooks} />
					</TabItem>
					{!isRelease && (
						<TabItem title={msg("analytics")}>
							<TransactionAnalytics key={`${project?.id}:${user?.id}:${session.id}:${currentSession.created_at}`} session={currentSession}
								projectId={responseContext?.projectId} userId={responseContext?.userId} ready={!busy && response?.data?.id === session.id && responseContext?.projectId === project?.id && responseContext?.userId === user?.id} />
						</TabItem>
					)}
					{!isRelease && canAnalyzeTransactionFailure(user?.id, currentSession.status) && (
						<TabItem title={msg("analysis")}>
							<TransactionFailureAnalysis analysis={failureAnalysis} />
						</TabItem>
					)}
					{!isRelease && (
						<TabItem title={msg("log")}>
							<PaymentSessionLog session={session} />
						</TabItem>
					)}
					{/* {!isRelease && canViewDectaTransactions && (!currentSession.sandbox || currentSession.sandbox === 0) && (
						<TabItem title={"TRX"}>
							<TRX session={currentSession} />
						</TabItem>
					)} */}
					{!isRelease && canViewDectaTransactions && (!currentSession.sandbox || currentSession.sandbox === 0) && (
						<TabItem title={"IC++"}>
							<InterchangeFees session={currentSession} />
						</TabItem>
					)}
					{!isRelease && (
						<TabItem title={msg("webhooks")}>
							<Webhooks session={currentSession} reloadTrigger={webhooksReloadTrigger} merchantNotifyUrl={merchantWebhooks?.notify} />
						</TabItem>
					)}
				</Tabs>
				<Spacer size="20px" />
				<HBox gap="10px">
					<Spacer grow />

					{!isRelease && (
						<Button
							variant="secondarySmall"
							onClick={() => {
								savePdf(currentSession);
							}}>
							PDF
						</Button>
					)}

					{!isRelease && canChangeSessionStatus && (
						<Button
							variant="primarySmall"
							onClick={() => {
								S_CHANGE_SESSION_STATUS_POPUP.invoke({ sessionId: currentSession.id, currentStatus: currentSession.status });
							}}>{msg("changeStatus_0fa09f9b")}</Button>
					)}

					{!isRelease && (
						<Button variant="primarySmall" onClick={handleForceWebhook}>{msg("forceWebhook_c97489c4")}</Button>
					)}

					{!isRelease && currentSession.type !== "refund" && currentSession.status === "completed" ? (
						<Button
							variant="primarySmall"
							onClick={() => {
								S_REFUND_POPUP.invoke({
									amount: currentSession.amount,
									sessionId: currentSession.id,
									method: "gate/refund",
									currency: currentSession.currency,
								});
							}}
							title={msg("refund_731464fb")}
						/>
					) : null}
				</HBox>
			</div>
		</Panel>
		{releaseTarget && releaseTarget.id === session.id && releaseTarget.projectId === project?.id && releaseTarget.userId === user?.id && canReleaseReserve &&
			<ReleaseReservePopup sessionId={releaseTarget.id} projectId={releaseTarget.projectId} userId={releaseTarget.userId} onClose={() => setReleaseTarget(null)} onSaved={() => { setReleaseTarget(null); GD.S_TRANSACTIONS_UPDATE.invoke(); }} />}
		</TransactionNavigationContext.Provider>
	);
};

declare interface TRawSectionProps {
	response: any;
	merchantWebhooks?: TMerchantWebhookSettings;
}

const RawSection: TFeaturedComponent<TRawSectionProps> = ({ response, merchantWebhooks }: TRawSectionProps) => {
	 useLocale();
	const visibleRawData = useMemo(() => getRawDataForDisplay(response?.data, merchantWebhooks), [merchantWebhooks, response?.data]);

	return (
		<Pre data={visibleRawData} wrap />
	);
};

RawSection.feature = "payment_raw_section";

const SessionDetails = ({ session, onOpenMerchant, onOpenSession, banContext, onReleaseReserve }: { onReleaseReserve?: () => void; session: IPaymentSession; onOpenMerchant: (merchantID: number) => void; onOpenSession?: (session: IPaymentSession) => void; banContext: { projectId: number; userId: number } | null }) => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const auth = GD.S_AUTH_STATUS.use([]);
	const { t, i18n } = useTranslation();
	//!TODO: hook?
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const [showDectaTransaction, setShowDectaTransaction] = useState(false);
	const [analysis, setAnalysis] = useState<{ sessionId: number; request: BankAnalysisRequest } | null>(null);
	const [openedStatement, setOpenedStatement] = useState<{ id: number; sessionId: number; projectId: number; userId: number } | null>(null);
	const canAnalyze = auth === AUTH_STATE.AUTHORIZED && !!banContext && project?.id === banContext.projectId && user?.id === banContext.userId
		&& OVERVIEW_ANALYSIS_USER_IDS.includes(banContext.userId) && (!session.project_id || session.project_id === banContext.projectId);
	useEffect(() => { setAnalysis(null); }, [session.id, project?.id, user?.id, canAnalyze]);

	useEffect(() => {
		setShowDectaTransaction(false);
		if (!session?.channel_id || !session?.processing_id) return;

		if (user && SETTINGS_ALLOWED_IDS.includes(user.id)) {
			const processingChannel = session.req?.merchant?.channels.find((ch: IPaymentSessionMerchantChannel) => ch.linked?.id === session.channel_id);
			setShowDectaTransaction(processingChannel?.integration?.common.type === "decta");
		}
	}, [session?.channel_id, session?.processing_id, session?.req?.merchant?.channels, user]);

	const userProject = project && user ? user.projects.find(val => val.id === project.id) : undefined;
	const showAdminControls = hasRole(userProject?.roles, "admin");
	const canOpenMerchant = hasPermission(userProject?.roles, "nav.clients") && !!session.mid;
	const canOpenStatements = auth === AUTH_STATE.AUTHORIZED && !!project && !!user && hasPermission(userProject?.roles, "statements.view")
		&& (!session.project_id || session.project_id === project.id);
	useEffect(() => { setOpenedStatement(null); }, [session.id, project?.id, user?.id, canOpenStatements]);

	if (!session) return <div>{msg("noSession")}</div>;
	const banAction = (type: string, value: unknown) => banContext && (!session.project_id || session.project_id === banContext.projectId)
		? <EntityBanAction key={`${session.id}:${type}:${String(value)}`} type={type} value={value} {...banContext} /> : null;
	const period = transactionAnalysisPeriod(session.created_at);
	const analysisValue = (type: "email" | "country" | "bank", value: unknown, display: ReactNode = getValue(value)) => {
		if (!canAnalyze || !banContext || !period || typeof value !== "string" || !value.trim()) return display;
		const title = type === "email" ? msg("topEmails") : type === "country" ? msg("topCountries") : msg("topBanks");
		const bank = getRankingAnalysisEntity(title, value.trim(), i18n.language);
		if (!bank) return display;
		const labelKey = type === "email" ? "analysis.openAnalysisEmail" : type === "country" ? "analysis.openAnalysisCountry" : "analysis.openAnalysisBank";
		const label = t(labelKey, { name: bank.label });
		return <EntityAnalysisLink type="button" aria-haspopup="dialog" aria-label={label} title={`${label} · ${period.from} – ${period.to} UTC`}
			onClick={event => { event.stopPropagation(); setAnalysis({ sessionId: session.id, request: { bank, ...period, currency: session.currency || "", ...banContext } }); }}>{display}</EntityAnalysisLink>;
	};

	const isRefund = session.type?.toLowerCase() === "refund";
	const isGreen = session.type?.toLowerCase() === "dms" || session.type?.toLowerCase() === "transfer";
	const amountText = formatPaymentAmount(session);
	const amountColor = isRefund ? "#DC2626" : isGreen ? "#16A34A" : undefined;
	const statusColor = getStatusColor(session.status);
	const hasPaymentError = !!session.fail_reason || ["failed", "declined", "rejected", "error", "expired", "canceled", "refund_declined"].includes(session.status?.toLowerCase());
	const processingChannel = session.req?.merchant?.channels.find((ch: IPaymentSessionMerchantChannel) => ch.linked?.id === session.channel_id);
	const card = session.payload?.card;
	const webhookTone = session.webhook_status?.toLowerCase() === "success" || session.webhook_status?.toLowerCase() === "completed" ? "success" : "warning";
	const channelId = getRouteId(session.channel_id);
	const terminalId = getRouteId(session.terminal_id);
	const refundPayment = session.children?.find(payment => payment.type?.toLowerCase() === "refund");
	const refundAmount = session.refund_amount ?? refundPayment?.amount;
	const refundCurrency = refundPayment?.currency ?? session.currency;
	const refundStatus = refundPayment?.status ?? session.refund_status;
	const hasRefundInfo = hasVisibleValue(refundAmount) || hasVisibleValue(refundStatus);
	const routeIntegration = processingChannel?.integration?.common?.type ?? session.channel_alias;
	const routeDescription = [
		channelId ? (
			<>{msg("channel_af0e6a90")}<CopyableText copyValue={channelId}>{channelId}</CopyableText>
			</>
		) : undefined,
		hasVisibleValue(routeIntegration) ? getValue(routeIntegration) : undefined,
	].filter(hasVisibleValue);
	const paymentInfoHasData = [
		session.channel_alias,
		channelId,
		processingChannel?.terminal?.name,
		processingChannel?.terminal?.type,
		terminalId,
		processingChannel?.integration?.common?.name,
		processingChannel?.integration?.common?.status,
		processingChannel?.linked?.processing_mid,
		session.processing_mid,
		processingChannel?.linked?.alias,
	].some(hasVisibleValue);

	return (
		<VBox gap="26px" sx={{ padding: "2px 0 6px" }}>
			<DetailsSurface>
				<HBox sx={{ alignItems: "flex-start", justifyContent: "space-between", gap: "14px" }}>
					<VBox gap="7px" sx={{ minWidth: 0 }}>
						<Typo variant="medium" sx={{ color: detailsColors.muted, fontWeight: 600 }}>{msg("paymentOverview")}</Typo>
						<Typo
							variant="medium"
							bold
							sx={{ color: amountColor ?? detailsColors.text, fontSize: "24px", lineHeight: "30px", wordBreak: "break-word" }}>
							{amountText}
						</Typo>
						<span style={{ color: detailsColors.muted, display: "block", fontSize: "12px", lineHeight: "17px", wordBreak: "break-word" }}>{msg("id")}<CopyableText copyValue={session.id}>{session.id}</CopyableText>
						</span>

						<span style={{ color: detailsColors.muted, display: "block", fontSize: "12px", lineHeight: "17px", wordBreak: "break-word" }}>{msg("merchantPaymentId")}<CopyableText copyValue={session.payment_id}>{session.payment_id}</CopyableText>
						</span>
					</VBox>
					<HBox gap="8px" sx={{ alignItems: "center", justifyContent: "flex-end", flexShrink: 0, flexWrap: "wrap" }}>
						<PaymentTypeBadge type={session.type} />
						<PaymentStatusBadge status={session.status} color={statusColor} />
						{session.sandbox > 0 && <Badge label={msg("sandbox")} color="#ff7348" />}
					</HBox>
				</HBox>

				<MetricGrid>
					<DetailCard title={msg("created")} value={<SessionDateTime value={session.created_at} />} subtitle={<>{msg("updated_a919c7c2")}<SessionDateTime value={session.updated_at} /></>} />
					<DetailCard
						title={msg("merchant")}
						value={session.req?.merchant?.name}
						onClick={canOpenMerchant ? () => onOpenMerchant(session.mid) : undefined}
						subtitle={
							<>
								MID <CopyableText copyValue={session.req?.merchant?.mid}>{session.req?.merchant?.mid}</CopyableText>
							</>
						}
					/>
					<DetailCard
						title={msg("payer")}
						value={session.req?.customer?.name ?? analysisValue("email", session.email)}
						subtitle={session.country ? analysisValue("country", session.country, <CountryValue code={session.country} />) : session.ip}
					/>
					<DetailCard
						title={msg("result")}
						value={
							<>
								{session.fail_reason || session.status}
								{hasPaymentError && hasVisibleValue(session.fail_code) && (
									<>
										<br />
										fail_code: {session.fail_code}
									</>
								)}
							</>
						}
						subtitle={session.related_transaction_id ? msg("related") + session.related_transaction_id : session.type}
						accent={statusColor}
					/>
					<DetailCard
						title={msg("settlement_711b5c6a")}
						value={"Status: " + getValue(session.settlement_status)}
						subtitle={<SessionDateTime value={session.settlement_date} />}
					/>
					{hasVisibleValue(session.reserve_release_date) ? (
						<DetailCard
							title={msg("rollingReserve_c2352be7")}
							action={onReleaseReserve && <ReserveReleaseAction title={msg("reserveReleaseTitle")} aria-label={msg("reserveReleaseTitle")} onClick={onReleaseReserve}><CalendarClock size={16} aria-hidden="true" /></ReserveReleaseAction>}
							value={"Amount: " + formatOptionalMajorAmount(session.rolling_reserve_amount, session.currency)}
							subtitle={<>{msg("releaseDate_f132be04")}<DateTimeTooltip value={session.reserve_release_date}>{formatSessionDateOnly(session.reserve_release_date)}</DateTimeTooltip></>}
						/>
					) : null}
				</MetricGrid>
			</DetailsSurface>

			<StatementDetails statement={session.statement_info} onOpenStatement={canOpenStatements ? id => setOpenedStatement({ id, sessionId: session.id, projectId: project.id, userId: user.id }) : undefined} />

			{session.instrument === "card" && card ? (
				<DashboardSection title={msg("paymentCard")}>
					<VBox gap="10px">
						<MetricGrid>
							<DetailCard title={msg("card")} value={card.mask} subtitle={`${card.network} (${card.type})`} />
							<DetailCard title={msg("bank")} value={analysisValue("bank", card.bank)} />
							<DetailCard title={msg("category")} value={card.category} />
							<DetailCard title={msg("country")} subtitle={card.country} value={analysisValue("country", card.countryCode, <CountryValue code={card.countryCode} />)} />
						</MetricGrid>
						<WideDetailRow title={msg("token")} value={card.token} copyValue={card.token} action={banAction("card_token", card.token)} />
						<WideDetailRow title={msg("bin")} value={card.bin} copyValue={card.bin} action={banAction("bin", card.bin)} />
					</VBox>
				</DashboardSection>
			) : (
				<DashboardSection title={msg("paymentInstrument")}>
					<MetricGrid>
						<DetailCard title={msg("instrument")} value={session.instrument} subtitle={session.instrument_name} />
						<DetailCard title={msg("type")} value={session.instrument_type} subtitle={session.instrument_data} />
						{session.institution && <DetailCard title={msg("bank")} value={analysisValue("bank", session.institution)} />}
						{session.instrument_country ? (
							<DetailCard
								title={msg("country")}
								value={analysisValue("country", session.instrument_country, <CountryValue code={session.instrument_country} />)}
								subtitle={getCountryName(session.instrument_country)}
							/>
						) : null}
					</MetricGrid>
				</DashboardSection>
			)}

			<DashboardSection title={msg("paymentRoute")}>
				<DetailsSurface padded={false}>
					<ActivityItem
						tone="neutral"
						title={
							(processingChannel?.provider?.name ?? session.channel_alias ?? msg("channel")) +
							" -> " +
							(processingChannel?.terminal?.name ?? msg("terminal"))
						}
						description={
							<>
								{routeDescription.length
									? routeDescription.map((item, index) => (
											<span key={index}>
												{index > 0 ? " • " : null}
												{item}
											</span>
										))
									: getValue(undefined)}
							</>
						}
						meta={processingChannel?.linked?.status ?? session.status}
					/>
					<ActivityItem
						tone={webhookTone}
						title={msg("webhookDelivery")}
						description={<>{msg("lastWebhookAt")}<SessionDateTime value={session.webhook_at} /></>}
						meta={session.webhook_status ?? emptyValue}
						last={!hasRefundInfo && (!showAdminControls || !session.processing_id)}
					/>
					{hasRefundInfo ? (
						<ActivityItem
							tone={getStatusTone(refundStatus)}
							title={msg("refund")}
							description={hasVisibleValue(refundAmount) ? formatRefundAmount(Number(refundAmount), refundCurrency) : msg("refundStatusUpdated")}
							meta={refundStatus ?? emptyValue}
							last={!showAdminControls || !session.processing_id}
						/>
					) : null}
					{showAdminControls && session.processing_id ? (
						<>
							<ActivityItem
								tone={showDectaTransaction ? "success" : "neutral"}
								title={msg("processingTransaction")}
								description={
									<>{msg("processingId")}<CopyableText copyValue={session.processing_id}>{session.processing_id}</CopyableText>
									</>
								}
								meta={showDectaTransaction ? "Decta" : undefined}
								last
							/>
						</>
					) : null}
				</DetailsSurface>
				{showAdminControls && showDectaTransaction && (
					<HBox>
						<Spacer grow />
						<Button
							variant="secondarySmall"
							title={msg("processingTransaction_716d3af6")}
							onClick={() => {
								S_DECTA_SESSION_POPUP.invoke({ sessionId: session.processing_id + "" });
							}}
						/>
					</HBox>
				)}
			</DashboardSection>

			<DashboardSection title={msg("payerDetails")}>
				<VBox sx={{ padding: "0" }}>
					<InfoRow label={msg("personName")} value={session.req?.customer?.name} />
					<InfoRow label={msg("eMail")} value={analysisValue("email", session.email)} action={banAction("email", session.email)} />
					<InfoRow label={msg("ip")} value={session.ip} action={banAction("ip", session.ip)} />
					<InfoRow label={msg("country")} value={analysisValue("country", session.country, <CountryValue code={session.country} />)} action={banAction("country", session.country)} last />
				</VBox>
			</DashboardSection>

			<DashboardSection title={msg("acquirerInfo")}>
				<VBox>
					<InfoRow label="ARN" value={session.acquirer_info?.arn ?? msg("notReadyYet")} />
					<InfoRow label="RRN" value={session.acquirer_info?.rrn ?? msg("notReadyYet")} last />
				</VBox>
			</DashboardSection>

			{showAdminControls && (
				<DashboardSection title={msg("paymentInfo")}>
					<MetricGrid empty={!paymentInfoHasData}>
						<DetailCard
							title={msg("channelAlias")}
							value={session.channel_alias}
							subtitle={
								channelId ? (
									<>{msg("channel_af0e6a90")}<CopyableText copyValue={channelId}>{channelId}</CopyableText>
									</>
								) : undefined
							}
						/>
						<DetailCard
							title={msg("terminal")}
							value={processingChannel?.terminal?.name ?? terminalId}
							subtitle={
								processingChannel?.terminal?.type ??
								(terminalId ? (
									<>
										ID <CopyableText copyValue={terminalId}>{terminalId}</CopyableText>
									</>
								) : undefined)
							}
						/>
						<DetailCard
							title={msg("integration")}
							value={processingChannel?.integration?.common?.name}
							subtitle={processingChannel?.integration?.common?.status}
						/>
						<DetailCard
							title={msg("processingMid_6d88f2c3")}
							value={
								<CopyableText copyValue={processingChannel?.linked?.processing_mid ?? session.processing_mid}>
									{processingChannel?.linked?.processing_mid ?? session.processing_mid}
								</CopyableText>
							}
							subtitle={processingChannel?.linked?.alias}
						/>
					</MetricGrid>
				</DashboardSection>
			)}

			<RelatedPaymentsSection title={msg("originalPayment")} payments={session.parent ? [session.parent] : null} onOpenSession={onOpenSession} />
			<RelatedPaymentsSection title={msg("relatedTransactions")} payments={session.children} onOpenSession={onOpenSession} />
			{canAnalyze && analysis?.sessionId === session.id && analysis.request.projectId === project?.id && analysis.request.userId === user?.id &&
				<BankAnalysisPopup request={analysis.request} onClose={() => setAnalysis(null)} onEntity={bank => setAnalysis(current => current ? { ...current, request: { ...current.request, bank } } : null)} />}
			{canOpenStatements && openedStatement?.sessionId === session.id && openedStatement.projectId === project?.id && openedStatement.userId === user?.id &&
				<StatementDialog statementID={openedStatement.id} onClose={() => setOpenedStatement(null)} />}
		</VBox>
	);
};

type TWebhook = {
	attempts: number;
	created_at: string;
	http_status: number;
	id: number;
	message: string;
	project_id: number;
	retries: number;
	sign: number;
	signal: number;
	ssid: number;
	text: string;
	type: string;
	url: string;
	yyyymm: number;
};

const Webhooks = ({ session, reloadTrigger = 0, merchantNotifyUrl }: { session: IPaymentSession; reloadTrigger?: number; merchantNotifyUrl?: string }) => {
	useLocale();
	const [response, _reload, _reset, _busy] = GD.REQ_API.useRequest(null, { method: "session/webhooks/list", data: { ssid: session.id } });

	useEffect(() => {
		if (!reloadTrigger) return;
		_reload({ method: "session/webhooks/list", data: { ssid: session.id } });
	}, [reloadTrigger, session.id]);

	return (
		<Table<TWebhook>
			cellPadding="5px 10px 5px 20px"
			headPadding="10px 10px 10px 20px"
			//
			order={["http_status", "created_at", "url", "message", "attempts"]}
			// order={["http_status", "created_at", "url", "message", "text", "attempts"]}
			data={response?.data}
			cellFactory={(row, cell, _index, _key, name) => {
				if (name === "url") return { content: getWebhookDisplayUrl(row.url, merchantNotifyUrl) || emptyValue };
				if (name === "created_at") {
					const d = parseSessionDate(cell);
					return {
						content: (
							<VBox sx={{ maxWidth: "200px" }}>
								<Typo variant="medium"><DateTimeTooltip value={cell}>{formatDisplayDate(d, { withZone: false })}</DateTimeTooltip></Typo>
								<Typo variant="small"><DateTimeTooltip value={cell}>{formatDisplayTime(d)}</DateTimeTooltip></Typo>
							</VBox>
						),
					};
				}
				if (name === "text") {
					const textType = (cell as any)?.type;
					return {
						content: textType ? <Typo variant="medium">{textType}</Typo> : null,
					};
				}
				return { content: null };
			}}
			onRowClick={row => {
				S_WEBHOOK_DETAIL_POPUP.invoke(getWebhookForDisplay(row, merchantNotifyUrl) as any);
			}}
		/>
	);
};

type TTrx = {
	id: number;
	yyyymmddhh: number;
	yyyymm: number;
	counterparty_account_id: number;
	account_id: number;
	amount: number;
	currency: string;
	status: string;
	created_at: string;
	updated_at: string;
	direction: string;
	balance: number;
	description: string;
	operation_id: number;
	type_id: number;
	reference_id: null;
	seq: number;
	meta: string;
	is_initial: number;
	user_id: number;
	project_id: number;
};

const TRX: TFeaturedComponent<{ session: any }> = ({ session }) => {
	useLocale();
	const [response, _reload, _reset, _busy] = GD.REQ_API.useRequest(null, { method: "session/trx/list", data: { ssid: session.id } });
	return (
		<Table<TTrx>
			//;
			gridTemplateColumns="auto auto auto 100px auto auto auto"
			order={["status", "id", "account_id", "description", "type_id", "amount", "balance"]}
			cellFactory={(row, cell, _index, _key, name, _expanded, _rowID) => {
				if (name === "id") {
					return {
						content: <TitledValue title={row.created_at} value={row.id} />,
					};
				}
				if (name === "account_id") {
					let mainDirection = row.direction === "expense" ? "From: " : "To: ";
					let oppositeDirection = row.direction === "expense" ? "To: " : "From: ";
					return {
						content: <TitledValue title={oppositeDirection + row.counterparty_account_id + ""} value={mainDirection + row.account_id} />,
					};
				}
				if (name === "status") {
					return { content: <Badge label={cell} /> };
				}
				if (name === "amount") {
					return { content: <Amount amount={cell} currency={row.currency} direction={row.direction} /> };
				}
				if (name === "balance") {
					return { content: <Amount amount={cell} currency={row.currency} direction={parseInt(cell) > 0 ? "income" : "expense"} /> };
				}
				if (name === "description") {
					return {
						content: (
							<Box>
								<Typo variant="medium" block sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{row.meta}
								</Typo>
								<Typo variant="medium" block sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{cell}
								</Typo>
							</Box>
						),
					};
				}
				return { content: null };
			}}
			data={response?.data}
		/>
	);
};

TRX.feature = "payment_trx_section";

const formatDectaBlobResponse = async (data: unknown) => {
	if (data instanceof Blob) {
		const text = await data.text();
		try {
			return JSON.stringify(JSON.parse(text), null, 2);
		} catch {
			return text;
		}
	}

	if (typeof data === "string") return data;
	return JSON.stringify(data, null, 2) ?? "";
};

const InterchangeFees: TFeaturedComponent<{ session: IPaymentSession }> = ({ session }) => {
	 useLocale();
	const { t } = useTranslation();
	const [responseText, setResponseText] = useState("");
	const [error, setError] = useState<IError>();
	const [busy, setBusy] = useState(true);
	const [attempt, setAttempt] = useState(0);
	const fees = useMemo(() => parseInterchangeData(responseText), [responseText]);

	useEffect(() => {
		let active = true;

		const loadFees = async () => {
			setBusy(true);
			setError(undefined);
			setResponseText("");

			if (!active) return;

			try {
				const merchant = session.req?.merchant;
				if (merchant?.mid) {
					const merchantReq = await GD.REQ_API.request({
						method: "merchants/get",
						data: { id: merchant.mid },
					});
					if (!active) return;
					if (merchantReq.error) { setError(merchantReq.error); return; }

					if (merchantReq.data) {
						const clientReq = await GD.REQ_API.request({
							method: "clients/get",
							data: { id: merchantReq.data.client_id },
						});
						if (!active) return;
						if (clientReq.error) { setError(clientReq.error); return; }

						const resp = await GD.REQ_API.request({
							method: "fees/ic" as IAPIMethod,
							data: {
								network: session.instrument_type,
								amount: session.amount,
								currency: session.currency,
								issuerCountry: session.instrument_country,
								merchantCountry: clientReq?.data?.country,
								cardCategory: session.payload?.card?.category,
								cardType: session.payload?.card?.type,
								irdTier: session.instrument_type === "mastercard" ? "ecom" : "",
								acceptance: session.instrument_type === "visa" ? "ecom" : undefined,
								mcc: merchant.mid,

								isTokenized: session.instrument === "ap" || session.instrument === "gp" ? true : false,
								isSecure: true,
								isMIT: false,
								hasDataQuality: true,
								isApproved: true,
							},
						});

						if (!active) return;

						if (resp?.error) {
							setError(resp.error);
							return;
						}

						const text = await formatDectaBlobResponse(resp?.data);
						if (!active) return;

						setResponseText(text);
					}
				}
			} catch {
				if (active) setError({ code: 0, message: t("interchange.loadError") });
			} finally {
				if (active) setBusy(false);
			}
		};

		loadFees();

		return () => {
			active = false;
		};
	}, [session.id, attempt]);

	return (
		<VBox gap="12px">
			<HBox sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
				<strong style={{ fontSize: 14 }}>{t("interchange.title")}</strong>
				<HBox gap="8px">
					<Button title={msg("raw")} variant="secondarySmall" icon={<FileJson size={14} aria-hidden="true" />} disabled={!responseText || busy} onClick={() => S_RAW_TEXT_POPUP.invoke({ title: `IC++ · ${session.id}`, value: responseText })} />
					<Button title={t("common.refresh")} variant="secondarySmall" icon={<RotateCw size={14} aria-hidden="true" />} disabled={busy} onClick={() => setAttempt(value => value + 1)} />
				</HBox>
			</HBox>
			<Error error={error} />
			<div role="status" aria-live="polite" aria-busy={busy} style={{ color: "#667085", fontSize: 13, ...(!busy && (error || fees) ? { position: "absolute", width: 1, height: 1, overflow: "hidden", clipPath: "inset(50%)" } : {}) }}>
				{busy && t("interchange.loading")}
				{!busy && !error && !fees && (responseText ? t("interchange.displayError") : t("interchange.unavailable"))}
			</div>
			{!busy && !error && fees && <InterchangeDetails data={fees} />}
		</VBox>
	);
};

InterchangeFees.feature = "payment_trx_section";
